import { FunctionComponent } from 'react';
import { Button, Card, Col, Empty, Form, Row, Tag, Typography } from 'antd';
import {
    BookOutlined,
    CalendarOutlined,
    LeftOutlined,
    RightOutlined,
    TeamOutlined,
    UserOutlined,
    ReadOutlined,
} from '@ant-design/icons';
import { ClassesEnseignantViewProps } from './types';
import { ClasseDtoF, JourActiviteEnum } from '../../../services/classe';
import { NiveauInterne } from '../../../services/inscription';

const { Text, Title } = Typography;

// ─── Mappings ──────────────────────────────────────────────────────────────────

const JOUR_LABELS: Record<JourActiviteEnum, string> = {
    [JourActiviteEnum.SAMEDI_MATIN]: 'Sam. matin',
    [JourActiviteEnum.DIMANCHE_MATIN]: 'Dim. matin',
    [JourActiviteEnum.DIMANCHE_APRES_MIDI]: 'Dim. après-midi',
    [JourActiviteEnum.MERCREDI_APRES_MIDI]: 'Mer. après-midi',
};

const NIVEAU_COLOR: Partial<Record<NiveauInterne, string>> = {
    [NiveauInterne.P1]: 'purple',
    [NiveauInterne.P2]: 'purple',
    [NiveauInterne.N1_1]: 'blue',
    [NiveauInterne.N1_2]: 'blue',
    [NiveauInterne.N2_1]: 'cyan',
    [NiveauInterne.N2_2]: 'cyan',
    [NiveauInterne.N3_1]: 'geekblue',
    [NiveauInterne.N3_2]: 'geekblue',
    [NiveauInterne.N4_1]: 'volcano',
    [NiveauInterne.N4_2]: 'volcano',
    [NiveauInterne.DEBUTANT]: 'green',
    [NiveauInterne.INTERMEDIAIRE]: 'orange',
    [NiveauInterne.AVANCE]: 'red',
};

// ─── Sub-components ────────────────────────────────────────────────────────────

interface YearNavProps {
    annee: number;
    onPrev: () => void;
    onNext: () => void;
}

const YearNav: FunctionComponent<YearNavProps> = ({ annee, onPrev, onNext }) => (
    <div className="mes-classes-year-nav">
        <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={onPrev}
            className="mes-classes-year-btn"
            {...({ testid: "annee-precedente" } as any)}
        />
        <span className="mes-classes-year-label">
            <CalendarOutlined style={{ marginRight: 8 }} />
            Année scolaire&nbsp;<strong>{annee}–{annee + 1}</strong>
        </span>
        <Button
            type="text"
            icon={<RightOutlined />}
            onClick={onNext}
            className="mes-classes-year-btn"
            {...({ testid: "annee-suivante" } as any)}
        />
    </div>
);

interface StatsBarProps {
    nbClasses: number;
    nbEleves: number;
}

const StatsBar: FunctionComponent<StatsBarProps> = ({ nbClasses, nbEleves }) => (
    <div className="mes-classes-stats-bar">
        <div className="mes-classes-stat-item">
            <BookOutlined className="mes-classes-stat-icon" />
            <span className="mes-classes-stat-value">{nbClasses}</span>
            <span className="mes-classes-stat-label">{nbClasses <= 1 ? 'classe' : 'classes'}</span>
        </div>
        <div className="mes-classes-stat-divider" />
        <div className="mes-classes-stat-item">
            <TeamOutlined className="mes-classes-stat-icon" />
            <span className="mes-classes-stat-value">{nbEleves}</span>
            <span className="mes-classes-stat-label">{nbEleves <= 1 ? 'élève' : 'élèves'}</span>
        </div>
    </div>
);

// ─── Main component ────────────────────────────────────────────────────────────

export const ClassesEnseignantDesktopView: FunctionComponent<ClassesEnseignantViewProps> = ({
    form,
    classes,
    debutAnneeScolaire,
    doSearchClasses,
    onConsulterClasse,
}) => {
    const currentAnnee: number = Form.useWatch('anneeDebut', form) ?? debutAnneeScolaire;

    const navigateYear = (delta: number) => {
        const newAnnee = currentAnnee + delta;
        form.setFieldValue('anneeDebut', newAnnee);
        doSearchClasses({ anneeDebut: newAnnee, anneeFin: newAnnee + 1 });
    };

    const totalEleves = classes.reduce(
        (acc, c) => acc + (c.liensClasseEleves?.length ?? 0),
        0,
    );

    const getEnseignantsDisplay = (classe: ClasseDtoF): string => {
        if (!classe.enseignants || classe.enseignants.length === 0) return 'Non renseigné';
        return classe.enseignants.map(e => e.nomPrenom).join(', ');
    };

    const getClasseCard = (classe: ClasseDtoF) => {
        const nbEleves = classe.liensClasseEleves?.length ?? 0;
        const niveauColor = classe.niveau ? (NIVEAU_COLOR[classe.niveau] ?? 'default') : 'default';

        return (
            <Col xs={24} sm={12} lg={8} key={'col_' + classe.id}>
                <Card
                    className="card-focus-effect mes-classes-card"
                    bordered
                    hoverable
                    title={
                        <div className="mes-classes-card-title-row">
                            <span style={{ fontWeight: 600, fontSize: 15 }}>{classe.libelle}</span>
                            {classe.niveau && (
                                <Tag color={niveauColor} className="mes-classes-niveau-tag">
                                    {classe.niveau}
                                </Tag>
                            )}
                        </div>
                    }
                >
                    {/* Enseignant */}
                    <div className="mes-classes-card-info-row">
                        <UserOutlined className="mes-classes-card-info-icon" />
                        <Text className="mes-classes-card-info-text">{getEnseignantsDisplay(classe)}</Text>
                    </div>

                    {/* Nombre d'élèves */}
                    <div className="mes-classes-card-info-row">
                        <TeamOutlined className="mes-classes-card-info-icon" />
                        <Text className="mes-classes-card-info-text">
                            <strong>{nbEleves}</strong>&nbsp;{nbEleves <= 1 ? 'élève inscrit' : 'élèves inscrits'}
                        </Text>
                    </div>

                    {/* Jours d'activités */}
                    {classe.activites && classe.activites.length > 0 && (
                        <div className="mes-classes-card-activites">
                            <CalendarOutlined className="mes-classes-card-info-icon" style={{ marginTop: 3 }} />
                            <div className="mes-classes-activites-tags">
                                {classe.activites.map(a => (
                                    <Tag key={a.jour} color="blue">
                                        {JOUR_LABELS[a.jour] ?? a.jour}
                                    </Tag>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* CTA */}
                    <div className="mes-classes-card-footer">
                        <Button
                            type="primary"
                            block
                            onClick={() => onConsulterClasse(classe.id!)}
                            style={{ backgroundColor: '#4FC3F7', borderColor: '#4FC3F7' }}
                        >
                            Consulter la classe
                        </Button>
                    </div>
                </Card>
            </Col>
        );
    };

    return (
        <Form form={form} initialValues={{ anneeDebut: debutAnneeScolaire }}>
            {/* Champ caché pour que useWatch fonctionne */}
            <Form.Item name="anneeDebut" hidden>
                <input type="hidden" />
            </Form.Item>

            <div className="mes-classes-page">
                {/* Hero — aligné sur le style AdminAdhesion */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '16px', paddingBottom: '16px', borderBottom: '3px solid #4FC3F7', marginBottom: '0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '48px', height: '48px', borderRadius: '10px', backgroundColor: '#4FC3F7', flexShrink: 0 }}>
                        <ReadOutlined style={{ fontSize: '24px', color: '#ffffff' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Title level={3} style={{ margin: 0, lineHeight: 1.2 }}>
                            Gestion de mes classes
                        </Title>
                        <Text type="secondary">Consultez et gérez vos classes pour l'année en cours</Text>
                    </div>
                    <YearNav
                        annee={currentAnnee}
                        onPrev={() => navigateYear(-1)}
                        onNext={() => navigateYear(1)}
                    />
                </div>

                {/* Stats bar */}
                {classes.length > 0 && (
                    <StatsBar nbClasses={classes.length} nbEleves={totalEleves} />
                )}

                {/* Grille */}
                <div className="mes-classes-grid-container">
                    {classes.length === 0 ? (
                        <div className="mes-classes-empty">
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <span>
                                        Aucune classe pour l'année scolaire&nbsp;
                                        <strong>{currentAnnee}–{currentAnnee + 1}</strong>
                                    </span>
                                }
                            >
                                <div className="mes-classes-empty-actions">
                                    <Button onClick={() => navigateYear(-1)} icon={<LeftOutlined />}>
                                        Année précédente
                                    </Button>
                                    <Button onClick={() => navigateYear(1)} icon={<RightOutlined />}>
                                        Année suivante
                                    </Button>
                                </div>
                            </Empty>
                        </div>
                    ) : (
                        <Row gutter={[20, 20]}>
                            {classes.map(classe => getClasseCard(classe))}
                        </Row>
                    )}
                </div>
            </div>
        </Form>
    );
};
